/**
 * useFocusTrap — Piège le focus à l'intérieur d'un conteneur (accessibilité modale).
 *
 * §11/§18 DA — hook a11y réutilisable pour toutes les modals.
 * Câblé dans ModalShell ; hérite à toute modal qui compose ModalShell.
 *
 * @param {React.RefObject} ref — ref sur le conteneur (ModalCard)
 * @param {boolean} active — activer/désactiver le trap
 *
 * @example
 *   const ref = useRef(null);
 *   useFocusTrap(ref, isOpen);
 *   return <ModalCard ref={ref}>...</ModalCard>;
 */
import { useEffect } from 'react';

const FOCUSABLE_SELECTORS = [
  'button:not(:disabled)',
  '[href]',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function useFocusTrap(ref, active) {
  useEffect(() => {
    if (!active || !ref.current) return;

    const el = ref.current;
    const focusable = Array.from(el.querySelectorAll(FOCUSABLE_SELECTORS));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    /* Focus le premier élément à l'ouverture */
    if (first) first.focus();

    function handleKeyDown(e) {
      if (e.key !== 'Tab') return;
      if (focusable.length === 0) { e.preventDefault(); return; }

      if (e.shiftKey) {
        /* Shift+Tab : si on est sur le premier → aller au dernier */
        if (document.activeElement === first) {
          e.preventDefault();
          if (last) last.focus();
        }
      } else {
        /* Tab : si on est sur le dernier → aller au premier */
        if (document.activeElement === last) {
          e.preventDefault();
          if (first) first.focus();
        }
      }
    }

    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [active, ref]);
}
